import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomModal, CustomInput, CustomSelect, CustomButton, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, Network, Trash2, Pencil, Settings, Eye, LayoutGrid, X, Tag } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { workflowApi } from '../api/workflowApi';
import { departmentApi } from '../api/departmentApi';
import { tagApi } from '../api/tagApi';
import { WorkflowNew, Department, Tag as TagType } from '../types';

const workflowSchema = z.object({
    department_id: z.string().min(1, 'Department is required'),
    name: z.string().min(2, 'Workflow name is required'),
    description: z.string().min(5, 'Description is required (min 5 chars)'),
    tag_ids: z.array(z.string()).min(1, 'At least one tag is required')
});

type WorkflowFormData = z.infer<typeof workflowSchema>;

export const Workflows = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [isLoading, setIsLoading] = useState(true);
    const [workflows, setWorkflows] = useState<WorkflowNew[]>([]);

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // Department filter
    const [deptFilter, setDeptFilter] = useState<string>('all');

    // Edit state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowNew | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [tagsList, setTagsList] = useState<TagType[]>([]);

    // Delete state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowNew | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm<WorkflowFormData>({
        resolver: zodResolver(workflowSchema),
        defaultValues: { tag_ids: [], department_id: '' }
    });

    const selectedTags = watch('tag_ids') || [];

    useEffect(() => {
        if (schoolSlug) {
            fetchMetaData();
        }
    }, [schoolSlug]);

    useEffect(() => {
        if (schoolSlug) {
            fetchWorkflows();
        }
    }, [schoolSlug, page, deptFilter]);

    const fetchWorkflows = async () => {
        setIsLoading(true);
        try {
            const filters: { department_id?: string; page?: number; page_size?: number } = {
                page,
                page_size: pageSize,
            };
            if (deptFilter !== 'all') {
                filters.department_id = deptFilter;
            }
            const response = await workflowApi.getWorkflows(schoolSlug, filters);
            if (response.success && response.data) {
                setWorkflows(response.data.items || []);
                setTotalPages(response.data.total_pages || 1);
                setTotalCount(response.data.total_count || 0);
                setHasNext(response.data.has_next || false);
                setHasPrevious(response.data.has_previous || false);
            }
        } catch (error) {
            showToast('Failed to load workflows', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMetaData = async () => {
        const [deptRes, tagsRes] = await Promise.all([
            departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 }),
            tagApi.getTags(schoolSlug, { page: 1, page_size: 50 })
        ]);
        if (deptRes.success && deptRes.data) {
            const data = deptRes.data as any;
            setDepartments(Array.isArray(data) ? data : (data.items || []));
        }
        if (tagsRes.success && tagsRes.data) {
            const data = tagsRes.data as any;
            setTagsList(Array.isArray(data) ? data : (data.items || []));
        }
    };

    const handleEditClick = (wf: WorkflowNew) => {
        setSelectedWorkflow(wf);
        reset({
            name: wf.name,
            description: wf.description,
            department_id: wf.department_id,
            tag_ids: wf.tags?.map(t => t.id) || []
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateWorkflow = async (data: WorkflowFormData) => {
        if (!schoolSlug || !selectedWorkflow) return;
        setIsUpdating(true);
        try {
            const response = await workflowApi.updateWorkflow(schoolSlug, selectedWorkflow.id, data);
            if (response.success) {
                showToast('Workflow updated successfully', 'success');
                setIsEditModalOpen(false);
                fetchWorkflows();
            } else {
                showToast(response.error || 'Failed to update workflow', 'error');
            }
        } catch (error) {
            showToast('Failed to update workflow', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteClick = (wf: WorkflowNew) => {
        setWorkflowToDelete(wf);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!schoolSlug || !workflowToDelete) return;
        setIsDeleting(true);
        try {
            const response = await workflowApi.deleteWorkflow(schoolSlug, workflowToDelete.id);
            if (response.success) {
                showToast('Workflow deleted successfully', 'success');
                setIsDeleteModalOpen(false);
                fetchWorkflows();
            } else {
                showToast(response.error || 'Failed to delete workflow', 'error');
            }
        } catch (error) {
            showToast('Failed to delete workflow', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const toggleTag = (id: string) => {
        if (selectedTags.includes(id)) {
            setValue('tag_ids', selectedTags.filter(t => t !== id), { shouldValidate: true });
        } else {
            setValue('tag_ids', [...selectedTags, id], { shouldValidate: true });
        }
    };

    const columns: CustomTableColumn<WorkflowNew>[] = [
        {
            header: 'Workflow Name',
            key: 'name',
            minWidth: '200px',
            render: (wf) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontWeight: '500' }}>{wf.name}</span>
                </div>
            )
        },
        {
            header: 'Department',
            key: 'department',
            minWidth: '150px',
            render: (wf) => (
                <span style={{
                    padding: '0.25rem 0.75rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)'
                }}>
                    {wf.departments?.name || wf.department_id}
                </span>
            )
        },
        {
            header: 'Description',
            key: 'description',
            minWidth: '220px',
            render: (wf) => <span style={{ color: 'var(--text-secondary)' }}>{wf.description || '-'}</span>
        },
        {
            header: 'Tags',
            key: 'tags',
            minWidth: '180px',
            render: (wf) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {wf.tags && wf.tags.length > 0 ? (
                        wf.tags.map(tag => (
                            <span
                                key={tag.id}
                                style={{
                                    padding: '0.2rem 0.5rem',
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '12px',
                                    fontSize: '0.7rem',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                {tag.name}
                            </span>
                        ))
                    ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>-</span>
                    )}
                </div>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '180px',
            width: '180px',
            render: (wf) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn primary"
                        title="Configure Levels"
                        onClick={() => navigate(`/${schoolSlug}/school-admin/departments/${wf.departments?.id || wf.department_id}/sub-departments/${wf.id}/levels`)}
                    >
                        <Settings size={18} />
                    </button>
                    <button
                        className="table-action-btn"
                        title="View Workflow"
                        onClick={() => navigate(`/${schoolSlug}/school-admin/workflows/${wf.id}/view`)}
                    >
                        <Eye size={18} />
                    </button>
                    <button
                        className="table-action-btn"
                        onClick={() => handleEditClick(wf)}
                        title="Edit Workflow"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={() => handleDeleteClick(wf)}
                        title="Delete Workflow"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Workflows', icon: <Network size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="Workflows Management"
                subtitle="Design and manage custom routing workflows for tickets."
                icon={<Network size={24} />}
                actionLabel="Create Workflow"
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/workflows/create`)}
            />

            {/* Responsive Filters */}
            <div className="table-filter-bar">
                <div className="table-filter-group">
                    <div className="table-select-box">
                        <CustomSearchableSelect
                            placeholder="Filter by Department"
                            options={[
                                { label: 'All Departments', value: 'all' },
                                ...departments.map(d => ({ label: d.name, value: d.id }))
                            ]}
                            value={deptFilter}
                            onChange={(val) => {
                                setDeptFilter(val as string);
                                setPage(1);
                            }}
                            hideAvatar
                        />
                    </div>
                </div>
            </div>

            <CustomTable
                columns={columns}
                data={workflows}
                isLoading={isLoading}
                isCompact={true}
                minWidth="940px"
                emptyMessage="No workflows found"
                emptyIcon={<Network size={32} />}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'workflows',
                }}
            />

            {/* Edit Workflow Modal */}
            <CustomModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Edit Workflow"
                size="large"
            >
                <form onSubmit={handleSubmit(handleUpdateWorkflow)} style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', paddingTop: '1rem' }}>
                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                            Assign Department <span style={{ color: 'var(--status-error)' }}>*</span>
                        </label>
                        <CustomSearchableSelect
                            options={departments.map(d => ({ label: d.name, value: d.id }))}
                            value={watch('department_id') || ''}
                            onChange={(val) => setValue('department_id', val as string, { shouldValidate: true })}
                            placeholder="Search and select department..."
                            leftIcon={<LayoutGrid size={18} />}
                            hideAvatar
                        />
                        {errors.department_id && <p style={{ color: 'var(--status-error)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.department_id.message}</p>}
                    </div>

                    <CustomInput
                        label="Workflow Name"
                        placeholder="e.g. Leave Request Flow"
                        error={errors.name?.message}
                        {...register('name')}
                        required
                    />

                    <CustomInput
                        label="Description"
                        placeholder="Purpose of this workflow"
                        error={errors.description?.message}
                        {...register('description')}
                        required
                    />

                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                            Associated Tags <span style={{ color: 'var(--status-error)' }}>*</span>
                        </label>

                        {/* Selected Tag Capsules */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: selectedTags.length > 0 ? '1rem' : '0' }}>
                            {selectedTags.map(id => {
                                const tag = tagsList.find(t => t.id === id);
                                if (!tag) return null;
                                return (
                                    <div
                                        key={id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            padding: '0.35rem 0.75rem',
                                            background: 'var(--accent-primary)',
                                            color: 'white',
                                            borderRadius: '16px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                                        }}
                                    >
                                        <Tag size={12} />
                                        {tag.name}
                                        <X
                                            size={14}
                                            style={{ cursor: 'pointer', marginLeft: '0.2rem' }}
                                            onClick={() => toggleTag(id)}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Searchable Tag Select */}
                        <CustomSearchableSelect
                            options={tagsList
                                .filter(t => !selectedTags.includes(t.id))
                                .map(t => ({ label: t.name, value: t.id }))}
                            onChange={(val) => toggleTag(val as string)}
                            placeholder="Search and select tags..."
                            leftIcon={<Tag size={18} />}
                            hideAvatar
                        />
                        {errors.tag_ids && <p style={{ color: 'var(--status-error)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.tag_ids.message}</p>}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                        <CustomButton type="button" variant="outline" color="secondary" onClick={() => setIsEditModalOpen(false)} disabled={isUpdating}>
                            Cancel
                        </CustomButton>
                        <CustomButton type="submit" variant="filled" color="primary" loading={isUpdating}>
                            Save Changes
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Workflow"
                message={`Are you sure you want to delete the workflow "${workflowToDelete?.name}"? This will remove all associated steps and levels. This action cannot be undone.`}
                confirmLabel="Delete"
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
